import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import DetallesFinalizados from './DetallesFinalizados';
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import { RiFileExcel2Line } from 'react-icons/ri';
import SearchBar from '../SearchBar';
import { exportToExcel } from './ExcelExporter';
import useUserRole from '../../../getRole';
import { useAuth } from '../../../context/AuthContext';

const ViajesFinalizados = () => {
    const [trips, setTrips] = useState([]);
    const [showIcons, setShowIcons] = useState(window.innerWidth <= 1450);
    const [filteredTrips, setFilteredTrips] = useState(trips);
    const { user } = useAuth();
    const userRole = useUserRole(user);
    const handleSearch = (searchTerm) => {
        if (!searchTerm.trim()) {
            setFilteredTrips(trips);
        } else {
            const filtered = trips.filter((trip) => {
                const conductor = trip.driver_data ? `${trip.driver_data.name} ${trip.driver_data.last_name}` : 'Sin nombre';
                const origen = trip.locations && trip.locations[0] ? trip.locations[0].description || 'N/A' : 'N/A';
                const destino = trip.locations && trip.locations[1] ? trip.locations[1].description || 'N/A' : 'N/A';
                const usuario = trip.user_data ? `${trip.user_data.name} ${trip.user_data.last_name}` : 'Sin usuario';
                const fecha = trip.time_request ? formatDate(trip.time_request) : 'N/A';
                const tripId = trip.trip_id.toLowerCase();
                return (
                    conductor.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    origen.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    destino.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    usuario.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    fecha.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    tripId.includes(searchTerm.toLowerCase())
                );
            });
            setFilteredTrips(filtered);
        }
    };

    useEffect(() => {
        setFilteredTrips(trips);
    }, [trips]);

    useEffect(() => {
        const fetchViajesData = async () => {
            try {
                const response = await axios.get(
                    process.env.REACT_APP_API_VIAJES,
                    {
                        headers: {
                            "x-hasura-admin-secret": process.env.REACT_APP_API_KEY,
                        },
                    }
                );

                const updatedTrips = response.data.trips.map((trip) => {
                    if (trip.status === "Terminado" && !trip.driver_data) {
                        return {
                            ...trip,
                            status: "Cancelado",
                        };
                    }
                    return trip;
                });

                setTrips(updatedTrips);
            } catch (error) {
                console.error("Error fetching trips data:", error);
            }
        };
        fetchViajesData();
    }, []);


    useEffect(() => {
        const handleResize = () => {
            setShowIcons(window.innerWidth <= 1450);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        const options = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        };
        return date.toLocaleString('en-US', options);
    };

    const getStatusClass = (status) => {
        if (status === "Terminado") {
            return "bg-greenE text-white text-sm";
        } else if (status === "Cancelado" || status === "Cancelada") {
            return "bg-redD text-white text-sm";
        } else if (status === "Aceptado") {
            return "bg-yellowE text-black";
        } else {
            return "";
        }
    };

    const getStatusContent = (row) => {
        if (row.status === null) {
            return 'N/A';
        }
        if (showIcons) {
            return row.status === "Terminado" ? (
                <AiOutlineCheck className="text-2xl" />
            ) : (
                <AiOutlineClose className="text-2xl" />
            );
        } else {
            return row.status;
        }
    };


    const columns = [
        {
            name: 'Viaje ID',
            selector: 'trip_id',
            sortable: true,
            width: '125px',
            cell: row =>
                <strong className='font-custom' style={{ display: 'block', fontSize: '1rem' }}>
                    {row.trip_id.substring(0, 8)}</strong>,
        },
        {
            name: 'Conductor',
            width: '200px',
            selector: row => {
                const conductorName = row.driver_data ? `${row.driver_data.name} ${row.driver_data.last_name}` : 'Sin Asignar';
                return conductorName;
            },
            sortable: true,
        },
        {
            name: 'Origen',
            width: '210px',
            selector: row => {
                const origen = (row.locations && row.locations[0]) ? row.locations[0].description || 'N/A' : 'N/A';
                return origen;
            },
            sortable: true,
        },
        {
            name: 'Destino',
            selector: row => {
                const destino = (row.locations && row.locations[1]) ? row.locations[1].description || 'N/A' : 'N/A';
                return destino;
            },
            width: '210px',
            sortable: true,
        },
        {
            name: 'Usuario',
            selector: row => {
                const userName = row.user_data ? `${row.user_data.name} ${row.user_data.last_name}` : 'N/A';
                return userName;
            },
            width: '200px',
            sortable: true,
        },
        {
            name: 'Fecha',
            selector: row => formatDate(row.time_request) || 'N/A',
            sortable: true,
            width: '220px',
        },
        {
            name: 'Estatus',
            selector: 'status',
            sortable: true,
            width: '140px',
            cell: row => (
                <span className={`px-3 py-1 text-lg text-center rounded-full ${getStatusClass(row.status)}`}>
                    {getStatusContent(row)}
                </span>
            ),
        },
        {
            name: 'Detalles',
            selector: 'options',
            width: '110px',
            cell: row => (
                <div className="flex items-center">
                    <DetallesFinalizados rowData={row} />
                </div>
            ),
            ignoreRowClick: true,
        },
    ];

    const columnConfig = [
        { label: 'Viaje ID', key: 'trip_id' },
        {
            label: 'Conductor', key: 'driver_name',
            transform: (value, rowData) => {
                const firstName = value && value.name ? value.name.trim() : '';
                const lastName = rowData.user_data && rowData.user_data.last_name ? rowData.user_data.last_name.trim() : '';
                return `${firstName} ${lastName}`;
            }
        },
        {
            label: 'Origen',
            key: 'locations[0].description',
            transform: (value) => {
                const originDescription = value || 'N/A';
                return originDescription;
            }
        },

        {
            label: 'Destino',
            key: 'locations[1].description',
            transform: (value) => {
                const destinoDescription = value || 'N/A';
                return destinoDescription;
            }
        },
        { label: 'Usuario', key: 'status' },
        {
            label: 'Usuario',
            key: 'user_data',
            transform: (value) => {
                if (value && value.name) {
                    const name = value.name;
                    const lastName = value.last_name || '';
                    return `${name} ${lastName}`;
                }
                return '';
            }
        },
        { label: 'Fecha', key: 'time_request' },
        { label: 'Estatus', key: 'status' },
    ];

    const exportToExcelLocal = () => {
        exportToExcel(filteredTrips, columnConfig);
    };

    return (
        <div className=' ml-30 mr-30 mt-10 mega:mr-T mega:ml-T items-center mb-4'>
            <SearchBar onSearch={handleSearch} />
            {(userRole === 'Admin' || userRole === 'Master') && (
                <button
                    className='bg-green-600 p-2 text-white font-bold mb-2 flex items-center space-x-2 hover:bg-green-900'
                    onClick={exportToExcelLocal}
                >
                    <RiFileExcel2Line className='text-2xl mr-1' />
                    Descargar Excel
                </button>
            )}
            <DataTable
                columns={columns}
                data={filteredTrips}
                pagination
                paginationPerPage={10} z
                paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
                highlightOnHover
                customStyles={{
                    rows: {
                        style: {
                            borderTop: '1px solid #CBD5E0',
                        },
                    },
                    headCells: {
                        style: {
                            fontSize: '1.25rem',
                            textAlign: 'center',
                        },
                    },
                    cells: {
                        style: {
                            marginTop: '1rem',
                            marginBottom: '1rem',
                            fontSize: '1rem'
                        },
                    },
                    pagination: {
                        style: {
                            fontSize: '1rem',
                            borderBottomLeftRadius: '2rem',
                            borderBottomRightRadius: '2rem',
                            color: 'black',
                        }
                    }
                }}
            />
        </div>
    );
}

export default ViajesFinalizados;